/**
 * EventsPage — admin-only `/events` view.
 *
 * Step 2 of #327: composes EventsToolbar + EventsTable + EventRow +
 * FeedbackDetailPanel. Owns the filter state, the parallel-expand
 * `Set<string>`, and the in-memory list that grows as the user clicks
 * "Aeltere laden". Refetches when any filter changes (resets expand-set + list).
 *
 * RBAC: Frontend gates via `hasRole('admin')` AND the API enforces it via
 * `requireRole('admin')`. Non-admin direct-navigation to `/events` sees the
 * "Zugriff verweigert" alert below; admins see the table.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../contexts/AuthContext';
import { eventsApi } from '../services/eventsApi';
import type { CreateIssueResponse, PortalEvent } from '../services/types';
import { TableSkeleton } from '../components/SkeletonLoaders';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';
import EventsToolbar, {
  type SourceFilter,
  type StatusFilter,
  type TypeFilter,
} from '../components/events/EventsToolbar';
import EventsTable from '../components/events/EventsTable';

const PAGE_LIMIT = 50;
const DEFAULT_REFRESH_INTERVAL_MS = 60_000;

interface EventsPageProps {
  /** NC-700: silent-poll interval. Default 60s; tests override with a small value. */
  refreshIntervalMs?: number;
}

const EventsPage: React.FC<EventsPageProps> = ({
  refreshIntervalMs = DEFAULT_REFRESH_INTERVAL_MS,
}) => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');

  const [type, setType] = useState<TypeFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [source, setSource] = useState<SourceFilter>('all');

  const [events, setEvents] = useState<PortalEvent[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [partial, setPartial] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initial / filter-change fetch: replace the whole list and reset expand-set.
  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await eventsApi.list({ type, status, source, limit: PAGE_LIMIT });
      setEvents(data.events);
      setNextBefore(data.nextBefore);
      setPartial(data.partial);
      setExpandedIds(new Set());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  }, [type, status, source]);

  useEffect(() => {
    if (!isAdmin) return;
    refetch();
  }, [isAdmin, refetch]);

  // NC-700: silent in-place refresh of existing rows so the status chip stays
  // in sync with the GH-issue-state reconciliation BE PR #713 added. We only
  // update events that are still on the first page; older paginated rows and
  // expand state are left untouched. Errors stay silent — the next tick or a
  // manual click surfaces them.
  const silentRefresh = useCallback(async () => {
    try {
      const data = await eventsApi.list({ type, status, source, limit: PAGE_LIMIT });
      const byId = new Map(data.events.map((e) => [e.id, e] as const));
      setEvents((prev) => prev.map((e) => byId.get(e.id) ?? e));
    } catch {
      // Silent: manual "Aktualisieren" still owns the visible error path.
    }
  }, [type, status, source]);

  useEffect(() => {
    if (!isAdmin) return;
    if (refreshIntervalMs <= 0) return;
    const tick = () => {
      // Don't burn the GH PAT budget while the admin's tab is in the background.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      silentRefresh();
    };
    const id = setInterval(tick, refreshIntervalMs);
    return () => clearInterval(id);
  }, [isAdmin, refreshIntervalMs, silentRefresh]);

  const loadMore = useCallback(async () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await eventsApi.list({
        type,
        status,
        source,
        before: nextBefore,
        limit: PAGE_LIMIT,
      });
      setEvents((prev) => [...prev, ...data.events]);
      setNextBefore(data.nextBefore);
      setPartial((prev) => prev || data.partial);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setLoadingMore(false);
    }
  }, [type, status, source, nextBefore, loadingMore]);

  // NC-637/NC-638: after the inline "Issue erstellen"-button succeeds, patch
  // the event in-place so the row flips to "link to existing issue" without a
  // full refetch. We trust the response's url/number because the API is the
  // single source of truth for the linkage.
  const handleIssueCreated = useCallback(
    (eventId: string, response: CreateIssueResponse) => {
      setEvents((prev) =>
        prev.map((e) =>
          e.id === eventId
            ? { ...e, githubIssueUrl: response.url, githubIssueNumber: response.number }
            : e,
        ),
      );
    },
    [],
  );

  const toggleRow = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (!isAdmin) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Zugriff verweigert — diese Seite ist nur fuer Admins zugaenglich.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h4">
          Feedback &amp; Fehler
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={refetch}
          disabled={loading || loadingMore}
        >
          Aktualisieren
        </Button>
      </Box>

      {/* NC-714: kurzer Hinweis-Text — Admins sollen auf einen Blick wissen,
          was diese Seite ist und wie sie damit Arbeit eroeffnen. */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Feedback- und Fehler-Einsendungen aus dem Portal. Aus relevanten
        Eintraegen kannst du via &laquo;GitHub-Issue erstellen&raquo; Arbeit
        eroeffnen — der Status spiegelt sich anschliessend automatisch wider.
      </Typography>

      <EventsToolbar
        type={type}
        status={status}
        source={source}
        onChange={(next) => {
          if (next.type !== undefined) setType(next.type);
          if (next.status !== undefined) setStatus(next.status);
          if (next.source !== undefined) setSource(next.source);
        }}
      />

      {partial && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Einige Datenquellen sind aktuell nicht erreichbar — die Liste ist
          eventuell unvollstaendig.
        </Alert>
      )}

      {loading ? (
        <TableSkeleton rows={5} columns={5} />
      ) : error ? (
        <ErrorMessage message={error} onRetry={refetch} />
      ) : events.length === 0 ? (
        <EmptyState
          variant="empty"
          title="Keine Eintraege"
          description="Mit den aktuellen Filtern wurden keine Feedback- oder Fehler-Eintraege gefunden."
        />
      ) : (
        <EventsTable
          events={events}
          expandedIds={expandedIds}
          onToggleRow={toggleRow}
          hasMore={nextBefore !== null}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
          onIssueCreated={handleIssueCreated}
        />
      )}
    </Box>
  );
};

export default EventsPage;
